/*
 * Tyre size bar - Tyre Souq Arabia
 * --------------------------------
 * The size search repeated at the top of the collection (results) page, so a
 * customer can change size without going back to the homepage.
 *
 * This is a COMPANION to assets/tyre-size-tap.js, not a replacement. That file
 * is live and proven, so nothing here modifies it - this script only drives the
 * widget instance it leaves on the form element (`el.__tyreTap`).
 *
 * It adds three things the homepage does not need:
 *   1. Pre-fill from the URL, so landing on ?...size_n.size_k=195/65R15 shows
 *      195 / 65 / R15 already chosen.
 *   2. Lazy loading of a field's options. On the homepage every list is loaded
 *      as a side effect of choosing the previous value; here the fields start
 *      pre-filled, so a list has to be fetched the first time a field is opened.
 *   3. Re-sync after the theme's AJAX facet filtering, which rewrites the grid
 *      and pushState's a new URL without firing any event we could listen to.
 *
 * NOTE on behaviour: changing the size deliberately RESETS every other filter
 * and the sort order. That is tyre-size-tap.js's go(), which rebuilds the URL
 * from availability + the new size alone. Confirmed as the wanted behaviour by
 * the shop owner on 2026-08-16 - a new size is a new search.
 *
 * Styling: assets/tyre-size-bar.css (bar chrome) + assets/tyre-size-tap.css
 * (the fields and panels themselves, shared with the homepage).
 */
(function () {
  'use strict';

  var SIZE_PARAM = 'filter.p.m.size_n.size_k';
  var ORDER = ['width', 'height', 'rim'];
  var PLACEHOLDER = { width: 'Width', height: 'Height', rim: 'Rim Size' };

  /* Reverse of tyre-size-tap.js's sizeValues().
     "195/65R15" -> 195 / 65 / 15
     "195/RR14"  -> 195 / R  / 14   (the no-aspect-ratio stock)
     "205R16"    -> 205 / -  / 16   (same stock, spelled the other way, so the
                                     height is simply unknown from the URL) */
  function parseSize(s) {
    if (!s) return null;
    var m = s.match(/^(\d+)\/(.+)R(\w+)$/);
    if (m) return { width: m[1], height: m[2], rim: m[3] };
    m = s.match(/^(\d+)R(\w+)$/);
    if (m) return { width: m[1], height: null, rim: m[2] };
    return null;
  }

  function currentSize() {
    var params = new URLSearchParams(window.location.search);
    return parseSize(params.get(SIZE_PARAM));
  }

  // Matches displayLabel() in tyre-size-tap.js - a bare "R" means nothing to a
  // customer, so it reads as "None".
  function shown(key, value) {
    if (key === 'height' && !/^\d/.test(value)) return 'None';
    return value;
  }

  function summaryText(size) {
    if (!size) return 'Choose your tyre size';
    if (!size.height) return size.width + ' R' + size.rim;
    if (!/^\d/.test(size.height)) return size.width + ' R' + size.rim;
    return size.width + '/' + size.height + ' R' + size.rim;
  }

  /* Write a size straight into the widget's state. Deliberately not using
     choose(), which would cascade, clear the fields downstream and auto-open
     the next panel - all wrong when we are restoring a size the customer
     already has. */
  function prefill(tap, size) {
    ORDER.forEach(function (key) {
      var f = tap.fields[key];
      if (!f) return;
      tap.selection[key] = '';
      f.labelEl.textContent = PLACEHOLDER[key];
      f.el.classList.remove('has-value');
    });

    if (!size) {
      tap.setEnabled('height', false);
      tap.setEnabled('rim', false);
      tap.syncResultButtons();
      return;
    }

    var pairs = [['width', size.width], ['height', size.height], ['rim', size.rim]];
    var stop = false;
    pairs.forEach(function (p) {
      var key = p[0];
      var val = p[1];
      if (stop) return;
      if (!val) { stop = true; return; }
      tap.selection[key] = val;
      tap.fields[key].labelEl.textContent = shown(key, val);
      tap.fields[key].el.classList.add('has-value');
      tap.setEnabled(key, true);
    });

    // Enable the field after the last one we filled, so it can be changed.
    var filled = ORDER.filter(function (k) { return tap.selection[k]; }).length;
    if (filled < ORDER.length) tap.setEnabled(ORDER[filled], true);
    ORDER.slice(filled + 1).forEach(function (k) { tap.setEnabled(k, false); });

    tap.syncResultButtons();
  }

  /* The homepage loads each list as a side effect of choosing the previous
     value. Here the fields arrive already filled, so the first time one is
     opened its panel would be empty - fetch on demand instead.
     tyre-size-tap.js calls stopPropagation (not stopImmediatePropagation) in
     its own handler, so a second listener on the same element still runs. */
  function lazyLoad(tap) {
    ORDER.forEach(function (key) {
      var f = tap.fields[key];
      if (!f) return;
      f.trigger.addEventListener('click', function () {
        if (tap.openKey === key && !f.options.children.length) tap.load(key);
      });
    });
  }

  function init() {
    var bar = document.querySelector('[data-tyre-size-bar]');
    if (!bar || bar.__tyreBar) return;
    var form = bar.querySelector('[data-tyre-tap]');
    var tap = form && form.__tyreTap;
    if (!tap) return false; // tyre-size-tap.js has not run yet
    bar.__tyreBar = true;

    var summary = bar.querySelector('[data-tyre-size-summary]');
    var toggle = bar.querySelector('[data-tyre-size-toggle]');

    /* `firstRun` matters. With no size chosen the bar opens itself, because
       then it IS the thing to use. But re-running that on every filter change
       would spring the bar back open after someone had deliberately collapsed
       it, which reads as the page fighting them. So it only auto-opens once. */
    function apply(firstRun) {
      var size = currentSize();
      prefill(tap, size);
      if (summary) summary.textContent = summaryText(size);
      bar.classList.toggle('has-size', !!size);
      if (!size && firstRun) bar.classList.add('is-open');
    }

    apply(true);
    lazyLoad(tap);

    if (toggle) {
      toggle.addEventListener('click', function () {
        var open = bar.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    /* The theme's facet filtering rewrites #ProductGridContainer and pushState's
       a new URL, without firing any event. Watching the grid is the only hook
       available, and it also covers back/forward. */
    var grid = document.getElementById('ProductGridContainer');
    if (grid && window.MutationObserver) {
      var last = window.location.search;
      new MutationObserver(function () {
        if (window.location.search === last) return;
        last = window.location.search;
        apply(false);
      }).observe(grid, { childList: true, subtree: false });
    }
    // Wrapped, not passed directly: popstate hands the listener an Event, which
    // is truthy and would be read as firstRun.
    window.addEventListener('popstate', function () {
      apply(false);
    });
    return true;
  }

  // tyre-size-tap.js is loaded first and both are deferred, so it has normally
  // already built the widget. Retry briefly rather than assume it.
  function boot() {
    if (init() !== false) return;
    var tries = 0;
    var t = setInterval(function () {
      if (init() !== false || ++tries > 40) clearInterval(t);
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
